import { useEffect, useMemo, useState } from 'react';
import { Formik } from 'formik';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setModals } from '@/store/actions/commonActions';
import { MODAL_TYPES } from '@/components/ModalProvider';
import { useGetTariffPlans } from '@/query/queries/fundraising/useGetTariffPlans';
import { FPG_ASSETS_STATUS } from '@/helpers/constants';
import { InlineAlert } from '@scramble/ui';
import { FR_STEPS } from '@/helpers/fundraising';
import { InfoMessage } from '@/components/InfoMessage';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { useModal } from '@/providers/modal';
import { LenderApplicationSelectModal } from '@/modals/LenderApplicationSelectModal';
import { LoanTermStatus } from '../LoanTerms';
import { FoundersConfirmation } from '../FoundersConfirmation';
import { FOUNDER_VALIDATION_SCHEMA } from '../../schema';
import { FundraisingSummary } from '../../components/FundraisingSummary';
import { useLenderSetFundraising } from '@/query/queries/fundraising/useLenderSetFundraising';
import { useLenderResetFundraising } from '@/query/queries/fundraising/useLenderResetFundraising';
import { useGetFundraisingDates } from '@/query/queries/fundraising/useGetFundraisingDates';
import { useGetLenderFundraisingStatusByCompanyId } from '@/query/queries/fundraising/useGetLenderFundraisingStatusByCompanyId';
import { useLenderSaveFpgAssets } from '@/query/queries/fundraising/useLenderSaveFpgAssets';
import { LenderStepFPGSelect } from '../LenderStepFPGSelect';
import { LenderStepPlanSelect } from '../LenderStepPlanSelect';
import { LenderStepFlexibilitySelect } from '../LenderStepFlexibilitySelect';
import IconChevron from './chevron-selector-vertical.svg';
import r from '@/kit/RoundButton/button.module.scss';
import s from './index.module.scss';

const STEPS = {
  tariff_select: 'tariff_select',
  fpg_select: 'fpg_select',
  flexibility_select: 'flexibility_select',
};

export const LenderFundraisingDetail = ({ applicationList }) => {
  const { state } = useLocation();
  const [step, setStep] = useState(null);
  const [application, setApplication] = useState(state?.application || null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { openSingleModal } = useModal();

  const companyId = application?.company.id;

  const { data: fundraisingDates } = useGetFundraisingDates();
  const { data: status } = useGetLenderFundraisingStatusByCompanyId(companyId);
  const { data: plans } = useGetTariffPlans(companyId);
  const { mutateAsync: setFundraising } = useLenderSetFundraising();
  const { mutateAsync: resetFundraising } = useLenderResetFundraising();
  const { mutateAsync: saveFpgAssets } = useLenderSaveFpgAssets();

  const upcomingRound = fundraisingDates?.id;
  const disabled = !status;

  useEffect(() => {
    if (!application && applicationList?.length) {
      setApplication(applicationList[0]);
    }
  }, [applicationList, application]);

  const manualTariffSet = useMemo(
    () => !plans?.find(plan => plan.id === status?.tariff_plan.id),
    [plans, status],
  );

  const { freeFpgOption } = useMemo(() => {
    return { freeFpgOption: status?.available_fpg_options.find(fpg => fpg.amount === 0) };
  }, [status]);

  if (!status) return null;

  const onSubmit = async (values, formik) => {
    const payload = {
      company_id: application?.company.id,
      new_funds: values.new_funds,
      has_accepted_terms: values.has_accepted_terms,
      fpg_option: values.fpg?.id,
      flexibility_option: values.flexibility_option?.id,
      tariff_plan: values.tariff_plan?.id,
    };

    if (
      freeFpgOption?.id === values.fpg?.id &&
      status?.fpg_assets_status === FPG_ASSETS_STATUS.new &&
      values.fpg_assets.length
    ) {
      await saveFpgAssets({ file_ids: values.fpg_assets.map(f => f.id) });
    }

    const res = await setFundraising(payload);
    if (res?.step === FR_STEPS.awaiting_borrower_guarantor) {
      setStep(null);
    }

    formik.setSubmitting(false);
  };

  const onReset = async () => {
    dispatch(
      setModals({
        type: MODAL_TYPES.confirm_modal,
        onConfirm: async () => {
          await resetFundraising({ company_id: application?.company.id });
          dispatch(setModals([]));
        },
        acceptLabel: t('fundraising:shared.reset'),
        cancelLabel: t('fundraising:shared.keep_the_goal'),
        title: t('fundraising:shared.changed_your_mind'),
        desc: t('fundraising:shared.opt_out_or_change_settings'),
      }),
    );
  };

  const canReset = Boolean(
    !status?.is_active &&
    [FR_STEPS.awaiting_borrower_guarantor, FR_STEPS.fundraising_summary].includes(status?.step),
  );

  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={FOUNDER_VALIDATION_SCHEMA(t, {
        maxNewFunds: status.max_new_funds || 0,
        freeFpgOption,
        fpgAssetsRequired: !!freeFpgOption && status?.fpg_assets_status === FPG_ASSETS_STATUS.new,
      })}
      initialValues={{
        new_funds: status.new_funds || 0,
        tariff_plan: status.tariff_plan || null,
        fpg: status.fpg_option
          ? status.fpg_option
          : status.available_fpg_options.find(option => option.is_default) || null,
        flexibility_option: status.flexibility_option
          ? status.flexibility_option
          : status.available_flexibility_options.find(option => option.is_default) || null,
        has_accepted_terms: false,
        fpg_assets: [],
      }}
    >
      {({ values, handleSubmit }) => (
        <form className={s.form} onSubmit={handleSubmit}>
          <div className="container-inner">
            <div className={s.wrapper}>
              <h1 className="f-hero-h2">{t('fundraising:page.title')}</h1>
              <div className={s.notification}>
                {status?.step === FR_STEPS.awaiting_lender && (
                  <InlineAlert
                    type="success"
                    title={t('fundraising:app_messages.fundraising_goal_ready')}
                    message={t('fundraising:app_messages.lender_calculation', {
                      company: application?.company.name,
                      round: application?.fundraising_name,
                    })}
                  />
                )}
                {status?.step === FR_STEPS.awaiting_borrower_guarantor && (
                  <InfoMessage
                    type="info"
                    title={t('fundraising:app_messages.proposal_submitted')}
                    description={t('fundraising:app_messages.proposal_submitted_desc')}
                  />
                )}
                {status?.step === FR_STEPS.fundraising_summary && (
                  <InfoMessage
                    type="success"
                    title={t('fundraising:app_messages.proposal_confirmed')}
                    description={t('fundraising:app_messages.proposal_confirmed_desc')}
                  />
                )}
              </div>

              <FundraisingSummary newFunds={status.new_funds} refinances={status.refinances}>
                <div className={s.control}>
                  <div
                    className={s.brand}
                    style={{ cursor: applicationList?.length > 1 ? 'pointer' : 'default' }}
                    onClick={() =>
                      applicationList?.length > 1 &&
                      openSingleModal(MODAL_IDS.lender_application_select_modal)
                    }
                  >
                    <div className={s.brand_icon}>
                      <img
                        src={application?.company.logo?.file}
                        alt={application?.company.name}
                        width={40}
                        height={40}
                      />
                    </div>
                    <div className={s.brand_content}>
                      <div className={s.brand_name}>
                        <span>{application?.company.name}</span>
                        {applicationList?.length > 1 && (
                          <img src={IconChevron} alt="chevron" width={16} height={16} />
                        )}
                      </div>
                      <div className={s.brand_desc}>
                        {t('fundraising:lender.funding_target', {
                          round: application?.fundraising_name,
                        })}
                      </div>
                    </div>
                  </div>

                  {canReset ? (
                    <button
                      type="button"
                      className={classnames(r.black_small, s.btn_reset)}
                      onClick={onReset}
                    >
                      {t('fundraising:shared.reset_or_edit_the_goal')}
                    </button>
                  ) : (
                    status?.step === FR_STEPS.awaiting_lender &&
                    upcomingRound === application?.fundraising_id && (
                      <button
                        type="button"
                        disabled={disabled}
                        className={classnames(r.yellow_base)}
                        onClick={() =>
                          manualTariffSet ? setStep(STEPS.fpg_select) : setStep(STEPS.tariff_select)
                        }
                      >
                        {t('fundraising:shared.proceed_with_this_goal')}
                      </button>
                    )
                  )}
                </div>
              </FundraisingSummary>
              {(status.step === FR_STEPS.fundraising_summary ||
                status.step === FR_STEPS.awaiting_borrower_guarantor) && (
                <>
                  <LoanTermStatus status={status} />
                  <FoundersConfirmation />
                </>
              )}
            </div>
          </div>

          {step === STEPS.tariff_select && (
            <LenderStepPlanSelect
              companyId={companyId}
              selected={values.tariff_plan}
              manualTariffSet={manualTariffSet}
              onBack={() => setStep(null)}
              onNext={() => setStep(STEPS.fpg_select)}
            />
          )}

          {step === STEPS.fpg_select && (
            <LenderStepFPGSelect
              companyId={companyId}
              status={status}
              disabled={disabled}
              manualTariffSet={manualTariffSet}
              freeFpgOption={freeFpgOption}
              onBack={() => (manualTariffSet ? setStep(null) : setStep(STEPS.tariff_select))}
              onNext={() => setStep(STEPS.flexibility_select)}
            />
          )}

          {step === STEPS.flexibility_select && (
            <LenderStepFlexibilitySelect
              companyId={companyId}
              status={status}
              disabled={disabled}
              freeFpgOption={freeFpgOption}
              onBack={() => setStep(STEPS.fpg_select)}
              onNext={() => setStep(STEPS.flexibility_select)}
            />
          )}

          {applicationList.length > 0 && (
            <LenderApplicationSelectModal
              list={applicationList}
              onSelect={app => setApplication(app)}
            />
          )}
        </form>
      )}
    </Formik>
  );
};
